import React, { useState, useEffect } from "react";
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
  TextField,
  IconButton,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { Dialog } from "../../../components/Dialogs/Dialog";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../hooks";
import {
  fetchAudienceListColumnsAsync,
  upsertAudienceListColumnAsync,
} from "../../../redux/marketCampaign/marketCampaignActions";
import { useBillingCompanyContext } from "../../../providers";

interface IMappedAudienceProps {
  open: boolean;
  onClose: () => void;
  uploadedHeaders: string[]; // Headers from uploaded Excel file
  previewListAction: (mapping_keys?: any[]) => void;
  existingMappings?: any[]; // Previously saved mappings
}

interface HeaderMapping {
  header: string;
  selectedMapping: string;
  isChecked: boolean;
  isMapped: boolean;
}

interface AddHeaderState {
  isAdding: boolean;
  newHeaderName: string;
}

const MappedAudience = (props: IMappedAudienceProps) => {
  const {
    open,
    onClose,
    uploadedHeaders,
    previewListAction,
    existingMappings = [],
  } = props;
  const dispatch = useDispatchWrapper();
  const companyOutlet = useBillingCompanyContext();

  // Get audience list columns from Redux state
  const { data: audienceListData } = useSelector(
    (state: IStoreState) => state.marketCampaign.audienceListColumns
  );

  // Combine master_columns and additional_columns for dropdown options
  const availableColumns = React.useMemo(() => {
    if (!audienceListData || !audienceListData.master_columns) {
      return [];
    }

    // Handle case where only master_columns are available (no additional_columns)
    const masterColumns = audienceListData.master_columns || [];
    const additionalColumns = audienceListData.additional_columns || [];

    return [...masterColumns, ...additionalColumns];
  }, [audienceListData]);

  // State for add header functionality
  const [addHeaderState, setAddHeaderState] = useState<AddHeaderState>({
    isAdding: false,
    newHeaderName: "",
  });

  // Initialize mapping state
  const [headerMappings, setHeaderMappings] = useState<HeaderMapping[]>([]);

  // Fetch columns when dialog opens
  useEffect(() => {
    if (open && companyOutlet?.comapny?.company_outlet_uuid) {
      dispatch(
        fetchAudienceListColumnsAsync(
          companyOutlet.comapny.company_outlet_uuid,
          (isSuccess, data) => {
            if (!isSuccess) {
              console.log("API not available yet, using empty columns");
            }
          }
        )
      );
    }
  }, [open, dispatch, companyOutlet?.comapny?.company_outlet_uuid]);

  // Initialize header mappings when uploaded headers change
  useEffect(() => {
    if (uploadedHeaders.length > 0) {
      const mappings: HeaderMapping[] = uploadedHeaders.map((header) => {
        // Check if this header has an existing mapping
        const existingMapping = existingMappings.find(
          (mapping) => mapping.column === header
        );

        if (existingMapping) {
          // Use existing mapping data
          return {
            header,
            selectedMapping: existingMapping.label || "",
            isChecked: true,
            isMapped: !!existingMapping.label,
          };
        } else {
          // Check if this header exists in available columns (auto-match)
          const matchingColumn = availableColumns.find((col) => col === header);
          return {
            header,
            selectedMapping: matchingColumn ? header : "",
            isChecked: true,
            isMapped: !!matchingColumn,
          };
        }
      });

      setHeaderMappings(mappings);
    }
  }, [uploadedHeaders, availableColumns, existingMappings]);

  // Separate mapped and unmapped headers
  const mappedHeaders = headerMappings.filter((mapping) => mapping.isMapped);
  const unmappedHeaders = headerMappings.filter((mapping) => !mapping.isMapped);

  // Get available options for dropdown (exclude already mapped options)
  const getAvailableOptions = (currentMapping: HeaderMapping) => {
    const alreadyMappedOptions = headerMappings
      .filter(
        (mapping) =>
          mapping.isMapped &&
          mapping.selectedMapping &&
          mapping.header !== currentMapping.header
      )
      .map((mapping) => mapping.selectedMapping);

    return availableColumns.filter(
      (column) => !alreadyMappedOptions.includes(column)
    );
  };

  // Get mandatory fields - only "phone" is mandatory
  const mandatoryFields = React.useMemo(() => {
    return ["phone"]; // Only phone is mandatory
  }, []);

  // Check if mandatory fields are mapped
  const getUnmappedMandatoryFields = () => {
    const mappedMandatoryFields = headerMappings
      .filter((mapping) => mapping.isMapped && mapping.selectedMapping)
      .map((mapping) => mapping.selectedMapping);

    return mandatoryFields.filter(
      (field) => !mappedMandatoryFields.includes(field)
    );
  };

  const unmappedMandatoryFields = getUnmappedMandatoryFields();
  const hasUnmappedMandatoryFields = unmappedMandatoryFields.length > 0;

  const handleCheckboxChange = (index: number, isMapped: boolean) => {
    setHeaderMappings((prev) =>
      prev.map((mapping, i) => {
        if (isMapped) {
          const mappedIndex = mappedHeaders.findIndex(
            (m) => m.header === mapping.header
          );
          return mappedIndex === index
            ? { ...mapping, isChecked: !mapping.isChecked }
            : mapping;
        } else {
          const unmappedIndex = unmappedHeaders.findIndex(
            (m) => m.header === mapping.header
          );
          return unmappedIndex === index
            ? { ...mapping, isChecked: !mapping.isChecked }
            : mapping;
        }
      })
    );
  };

  const handleDropdownChange = (
    index: number,
    value: string,
    isMapped: boolean
  ) => {
    setHeaderMappings((prev) =>
      prev.map((mapping, i) => {
        if (isMapped) {
          const mappedIndex = mappedHeaders.findIndex(
            (m) => m.header === mapping.header
          );
          return mappedIndex === index
            ? { ...mapping, selectedMapping: value, isMapped: !!value }
            : mapping;
        } else {
          const unmappedIndex = unmappedHeaders.findIndex(
            (m) => m.header === mapping.header
          );
          return unmappedIndex === index
            ? { ...mapping, selectedMapping: value, isMapped: !!value }
            : mapping;
        }
      })
    );
  };

  const handleAddHeader = () => {
    if (addHeaderState.newHeaderName.trim()) {
      dispatch(
        upsertAudienceListColumnAsync(
          addHeaderState.newHeaderName.trim(),
          companyOutlet.comapny.company_outlet_uuid,
          (updatedData) => {
            // Refresh the columns after adding
            dispatch(
              fetchAudienceListColumnsAsync(
                companyOutlet.comapny.company_outlet_uuid
              )
            );
            setAddHeaderState({ isAdding: false, newHeaderName: "" });
          }
        )
      );
    }
  };

  const handleSave = () => {
    // Check if all mandatory fields are mapped
    if (hasUnmappedMandatoryFields) {
      // Show error message - you can replace this with a proper notification
      alert(
        `Please map the following mandatory fields: ${unmappedMandatoryFields.join(
          ", "
        )}`
      );
      return;
    }

    // Only include mapped data (where isMapped is true and selectedMapping is not empty)
    const mapping_keys = headerMappings
      .filter((mapping) => mapping.isMapped && mapping.selectedMapping)
      .map((mapping) => ({
        check: mapping.isChecked ? 1 : 0,
        label: mapping.selectedMapping, // Changed: label is now selectedMapping
        column: mapping.header, // Changed: column is now header
      }));

    // Save mapping keys to parent component instead of calling API
    previewListAction(mapping_keys);
    onClose();
  };

  const renderHeaderTable = (
    headers: HeaderMapping[],
    title: string,
    isMapped: boolean
  ) => (
    <>
      <Typography variant="h3" fontWeight="bold" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Paper sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {/* Left Column - Headers */}
          <Grid item xs={6}>
            <Typography variant="body2" fontWeight="700" sx={{ mb: 1 }}>
              Uploaded Headers
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {headers.map((mapping, index) => (
                <Box
                  key={mapping.header}
                  sx={{
                    p: 1,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    backgroundColor: "#f9f9f9",
                    height: "56px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    {mapping.header}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Right Column - Mapping Controls */}
          <Grid item xs={6}>
            <Typography variant="body2" fontWeight="700" sx={{ mb: 1 }}>
              Mapping Controls
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {headers.map((mapping, index) => (
                <Box
                  key={`mapping-${mapping.header}`}
                  sx={{
                    p: 1,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    backgroundColor: "#ffffff",
                    height: "56px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={mapping.isChecked}
                        onChange={() => handleCheckboxChange(index, isMapped)}
                        size="small"
                      />
                    }
                    label=""
                    sx={{ m: 0, minWidth: "auto" }}
                  />

                  <FormControl fullWidth size="small">
                    <Select
                      value={mapping.selectedMapping}
                      onChange={(e) =>
                        handleDropdownChange(
                          index,
                          e.target.value as string,
                          isMapped
                        )
                      }
                      displayEmpty
                      sx={{ minWidth: "200px" }}
                    >
                      <MenuItem value="">
                        <em>
                          {getAvailableOptions(mapping).length > 0
                            ? "Select mapping"
                            : "No columns available"}
                        </em>
                      </MenuItem>
                      {getAvailableOptions(mapping).map((column) => {
                        const isPhoneNotMapped =
                          column === "phone" && hasUnmappedMandatoryFields;
                        return (
                          <MenuItem
                            key={column}
                            value={column}
                            sx={{
                              backgroundColor: "inherit",
                              "&:hover": {
                                backgroundColor: "inherit",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={
                                  isPhoneNotMapped ? "bold" : "normal"
                                }
                                color={"inherit"}
                              >
                                {column}
                              </Typography>
                              {isPhoneNotMapped && (
                                <Typography variant="caption">
                                  (Required)
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>

                  {!isMapped && (
                    <IconButton
                      size="small"
                      onClick={() =>
                        setAddHeaderState({ isAdding: true, newHeaderName: "" })
                      }
                      sx={{ ml: 1 }}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Map Audience Headers"
      size="md"
    >
      <DialogContent>
        {/* Mandatory Fields Warning */}
        {hasUnmappedMandatoryFields && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#fff3cd",
              border: "1px solid #ffeaa7",
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body2"
              color="warning.dark"
              fontWeight="medium"
            >
              ⚠️ Required Mapping: Please map the following mandatory fields:{" "}
              <strong>{unmappedMandatoryFields.join(", ")}</strong>
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              These fields are required for audience processing and cannot be
              skipped.
            </Typography>
          </Box>
        )}

        {/* Mapped Headers Table */}
        {mappedHeaders.length > 0 &&
          renderHeaderTable(mappedHeaders, "Mapped Headers", true)}

        {/* Unmapped Headers Table */}
        {unmappedHeaders.length > 0 &&
          renderHeaderTable(unmappedHeaders, "Unmapped Headers", false)}

        {/* Add Header Dialog */}
        {addHeaderState.isAdding && (
          <Paper
            sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2, mb: 2 }}
          >
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Add New Header
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Enter header name"
                value={addHeaderState.newHeaderName}
                onChange={(e) =>
                  setAddHeaderState((prev) => ({
                    ...prev,
                    newHeaderName: e.target.value,
                  }))
                }
                sx={{ flexGrow: 1 }}
              />
              <Button
                size="small"
                variant="contained"
                onClick={handleAddHeader}
                disabled={!addHeaderState.newHeaderName.trim()}
              >
                Add
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setAddHeaderState({ isAdding: false, newHeaderName: "" })
                }
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={hasUnmappedMandatoryFields}
          sx={{
            backgroundColor: hasUnmappedMandatoryFields
              ? "grey.300"
              : "primary.main",
            "&:hover": {
              backgroundColor: hasUnmappedMandatoryFields
                ? "grey.300"
                : "primary.dark",
            },
          }}
        >
          {hasUnmappedMandatoryFields
            ? `Map Required Fields (${unmappedMandatoryFields.length} remaining)`
            : "Save Mappings"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MappedAudience;
